import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {injectIntl, intlShape, defineMessages} from 'react-intl';
import VM from 'scratch-vm';

import spriteLibraryContent from '../lib/libraries/sprites.json';
import randomizeSpritePosition from '../lib/randomize-sprite-position';
import spriteTags from '../lib/libraries/sprite-tags';

import LibraryComponent from '../components/library/library.jsx';

const messages = defineMessages({
    libraryTitle: {
        defaultMessage: 'Choose a Sprite',
        description: 'Heading for the sprite library',
        id: 'gui.spriteLibrary.chooseASprite'
    }
});

const SPRITE_CATEGORY_ORDER = spriteTags.map(tag => tag.tag);
const spriteCategoryMessages = spriteTags.reduce((acc, tag) => {
    acc[tag.tag] = tag.intlLabel;
    return acc;
}, {
    other: 'Other'
});

const getSpriteCategory = item => {
    const tags = (item && item.tags || []).map(tag => String(tag).toLowerCase());
    return SPRITE_CATEGORY_ORDER.find(tag => tags.includes(tag)) || 'other';
};

const spriteLibraryContentWithCategories = spriteLibraryContent.map(item => Object.assign({}, item, {
    category: getSpriteCategory(item)
}));

const postScratchLog = message => {
    try {
        if (window.ScratchLog && typeof window.ScratchLog.postMessage === 'function') {
            window.ScratchLog.postMessage(String(message));
        }
    } catch (e) {
        // Ignore logging bridge errors.
    }
};

const normalizeSpriteLibraryItem = item => {
    const sprite = JSON.parse(JSON.stringify(item || {}));
    delete sprite.category;
    sprite.isStage = false;
    sprite.variables = sprite.variables || {};
    sprite.lists = sprite.lists || {};
    sprite.broadcasts = sprite.broadcasts || {};
    sprite.blocks = sprite.blocks || {};
    sprite.comments = sprite.comments || {};
    if (typeof sprite.currentCostume !== 'number') sprite.currentCostume = 0;
    if (typeof sprite.volume !== 'number') sprite.volume = 100;
    if (typeof sprite.visible !== 'boolean') sprite.visible = true;
    if (typeof sprite.size !== 'number') sprite.size = 100;
    if (typeof sprite.direction !== 'number') sprite.direction = 90;
    if (typeof sprite.draggable !== 'boolean') sprite.draggable = false;
    sprite.rotationStyle = sprite.rotationStyle || 'all around';
    sprite.costumes = (sprite.costumes || []).map((costume, index) => {
        const nextCostume = Object.assign({}, costume);
        const dataFormat = nextCostume.dataFormat || nextCostume.format || 'png';
        const md5ext = nextCostume.md5ext || nextCostume.md5 || nextCostume.baseLayerMD5 ||
            (nextCostume.assetId ? `${nextCostume.assetId}.${dataFormat}` : '');
        nextCostume.name = nextCostume.name || nextCostume.costumeName || `costume${index + 1}`;
        nextCostume.md5ext = md5ext;
        if (!nextCostume.assetId && md5ext) nextCostume.assetId = md5ext.split('.')[0];
        nextCostume.dataFormat = dataFormat;
        return nextCostume;
    });
    sprite.sounds = (sprite.sounds || []).map((sound, index) => {
        const nextSound = Object.assign({}, sound);
        const dataFormat = nextSound.dataFormat || 'wav';
        const md5ext = nextSound.md5ext || nextSound.md5 ||
            (nextSound.assetId ? `${nextSound.assetId}.${dataFormat}` : '');
        nextSound.name = nextSound.name || nextSound.soundName || `sound${index + 1}`;
        nextSound.md5ext = md5ext;
        if (!nextSound.assetId && md5ext) nextSound.assetId = md5ext.split('.')[0];
        nextSound.dataFormat = dataFormat;
        if (typeof nextSound.format === 'undefined') nextSound.format = '';
        return nextSound;
    });
    return sprite;
};

class SpriteLibrary extends React.PureComponent {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleItemSelect'
        ]);
    }
    handleItemSelect (item) {
        const spriteItem = normalizeSpriteLibraryItem(item);
        // Randomize position of library sprite
        randomizeSpritePosition(spriteItem);
        this.props.vm.addSprite(JSON.stringify(spriteItem)).then(() => {
            this.props.onActivateBlocksTab();
        }).catch(error => {
            const message = error && error.stack ? error.stack : error && error.message ? error.message : error;
            // eslint-disable-next-line no-console
            console.error('[MaiPai Scratch] add sprite failed', error);
            postScratchLog(`[MaiPai Scratch] add sprite failed: ${message}`);
        });
    }
    render () {
        return (
            <LibraryComponent
                categoryLabels={spriteCategoryMessages}
                categoryOrder={SPRITE_CATEGORY_ORDER}
                data={spriteLibraryContentWithCategories}
                id="spriteLibrary"
                tags={spriteTags}
                title={this.props.intl.formatMessage(messages.libraryTitle)}
                withCategories
                onItemSelected={this.handleItemSelect}
                onRequestClose={this.props.onRequestClose}
            />
        );
    }
}

SpriteLibrary.propTypes = {
    intl: intlShape.isRequired,
    onActivateBlocksTab: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func,
    vm: PropTypes.instanceOf(VM).isRequired
};

export default injectIntl(SpriteLibrary);
